import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

type ToastType = 'success' | 'error' | 'info'

interface Toast {
  id: number
  message: string
  type: ToastType
  fading: boolean
}

interface ToastState {
  toast: (message: string, type?: ToastType) => void
}

// Exported so the EmailVerifyBanner can fall back gracefully when the
// banner is mounted inside Shell during a test that doesn't wrap with
// ToastProvider (see Shell.test.tsx). Production always provides one.
export const ToastContext = createContext<ToastState | null>(null)

let nextId = 0
const VISIBLE_MS = 2500
const FADE_MS = 400

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common')
  const [toasts, setToasts] = useState<Toast[]>([])

  const startFade = useCallback((id: number) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, fading: true } : t))
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, FADE_MS)
  }, [])

  const toast = useCallback((message: string, type: ToastType = 'success') => {
    const id = ++nextId
    setToasts(prev => [...prev, { id, message, type, fading: false }])
    setTimeout(() => startFade(id), VISIBLE_MS)
  }, [startFade])

  const dismiss = useCallback((id: number) => {
    startFade(id)
  }, [startFade])

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}

      {/* Toast container. role="status" + aria-live="polite" announces
          appended children to assistive tech (WCAG 4.1.3). Per-toast
          role="alert" on errors bumps the announcement to assertive so
          failures aren't queued behind chatty success toasts. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      >
        {/* The loop variable is `toast` (not `t`) so the i18n t()
            function from useTranslation stays accessible inside the
            JSX body. */}
        {toasts.map(toast => (
          <div
            key={toast.id}
            role={toast.type === 'error' ? 'alert' : undefined}
            className={`
              pointer-events-auto flex items-center gap-2.5 rounded-2xl border px-4 py-3 text-sm font-medium
              backdrop-blur-[10px] shadow-v2-card transition-all ease-in-out dark:shadow-v2-card-dark
              ${toast.fading ? 'translate-x-4 opacity-0' : 'translate-x-0 animate-[slideIn_0.2s_ease-out] opacity-100'}
              ${toast.type === 'success' ? 'border-emerald-500/40 bg-gradient-to-r from-emerald-500/95 to-teal-500/95 text-white' : ''}
              ${toast.type === 'error' ? 'border-red-500/40 bg-gradient-to-r from-red-500/95 to-rose-500/95 text-white' : ''}
              ${toast.type === 'info' ? 'border-white/70 bg-white/80 text-v2-ink dark:border-[rgba(230,237,243,0.15)] dark:bg-v2-bg-dark/90 dark:text-v2-ink-inv' : ''}
            `}
            style={{ transitionDuration: `${FADE_MS}ms` }}
          >
            {toast.type === 'success' && <CheckCircle2 size={16} className="shrink-0" />}
            {toast.type === 'error' && <AlertCircle size={16} className="shrink-0" />}
            {toast.type === 'info' && <Info size={16} className="shrink-0 text-v2-purple dark:text-v2-purple-dark" />}
            <span>{toast.message}</span>
            <button
              onClick={() => dismiss(toast.id)}
              aria-label={t('toast.dismiss_label')}
              className="ml-1 shrink-0 rounded-full p-0.5 opacity-70 transition hover:bg-black/10 hover:opacity-100 dark:hover:bg-white/10"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

// useOptionalToast returns the toast API if a ToastProvider is mounted,
// otherwise a no-op. Used by leaf components (e.g. ExerciseBlock) that
// also render in playground / test contexts without a provider — they
// shouldn't crash just because they want to surface an error.
export function useOptionalToast(): ToastState {
  const ctx = useContext(ToastContext)
  return ctx ?? { toast: () => {} }
}
